import { Combobox } from '@headlessui/react'
import clsx from 'clsx'
import { LucideCheck, LucideChevronDown, LucideX } from 'lucide-react'
import React, { useRef } from 'react'
import { useControlField, useField } from 'remix-validated-form'

import { Badge } from '~/components/ui'
import FormFieldContainer from './FieldContainer'

type SelectMultipleFieldProps = {
  className?: string
  name: string
  label: string
  required?: boolean
  limitSelectionNumber?: number
  options: string[] | { [group: string]: string[] }
}

export default function SelectMultipleField(props: SelectMultipleFieldProps) {
  const { className, name, label, options } = props
  const limitSelectionNumber = props.limitSelectionNumber || 999
  const { error, getInputProps } = useField(name)
  const { onBlur } = getInputProps()

  const [values, setValues] = useControlField<string[] | undefined>(name)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const renderOptions = (options: string[]) => {
    if ((values || []).length < limitSelectionNumber) {
      return options.map((value, index) => (
        <Combobox.Option
          key={index}
          value={value}
          className={({ active }) =>
            clsx(
              'relative cursor-pointer select-none py-2 pl-6 pr-9 text-foreground',
              active && 'bg-muted'
            )
          }>
          {({ active, selected }) => (
            <>
              <span
                className={clsx('block truncate', selected && 'font-semibold')}>
                {value}
              </span>

              {selected && (
                <span
                  className={clsx(
                    'absolute inset-y-0 right-0 flex items-center pr-4 text-foreground'
                    // active ? 'text-white' : 'text-sky-600'
                  )}>
                  <LucideCheck className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
            </>
          )}
        </Combobox.Option>
      ))
    } else {
      return (
        <p className="py-2 pl-6 pr-9 text-foreground">
          Maximum number of selections reached
        </p>
      )
    }
  }

  const renderGroups = (options: { [group: string]: string[] }) => {
    return Object.keys(options).map((group, index) => {
      return (
        <React.Fragment key={index}>
          <li key={group} className="px-3 py-2 font-semibold">
            <span>{group}</span>
          </li>
          {renderOptions(options[group])}
        </React.Fragment>
      )
    })
  }

  return (
    <FormFieldContainer {...props} id={name} className={className}>
      <div className="relative">
        <Combobox
          value={values || []}
          onChange={(v) => {
            setValues(v)
            onBlur?.()
          }}
          multiple
          name={name}
          disabled={
            Array.isArray(options)
              ? options.length === 0
              : Object.keys(options).length === 0
          }>
          <div
            className="focus-ring-primary flex w-full  cursor-default flex-wrap gap-2 rounded-md border bg-background py-1.5 pl-3 pr-10 shadow-sm"
            onClick={() => buttonRef.current?.click()}>
            {(values || []).length === 0 && (
              <span className="my-0.5 select-none text-sm text-gray-500">
                Select {label}...
              </span>
            )}
            {(values || []).map((value, index) => {
              return (
                <Badge key={index} className="py-px">
                  {value}
                  <button
                    className=""
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const newValues = [...(values || [])]
                      newValues.splice(index, 1)
                      setValues(newValues)
                      onBlur?.()
                    }}>
                    <LucideX
                      className="-mr-1 ml-2 h-3 w-3"
                      aria-hidden="true"
                    />
                  </button>
                </Badge>
              )
            })}
          </div>

          <Combobox.Button
            ref={buttonRef}
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <LucideChevronDown
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
          </Combobox.Button>

          <Combobox.Options className="absolute z-30 mt-1 max-h-96 w-full overflow-auto rounded-md bg-background py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {Array.isArray(options)
              ? renderOptions(options)
              : renderGroups(options)}
          </Combobox.Options>
        </Combobox>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </FormFieldContainer>
  )
}
